// External dependencies
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Internal dependencies
import { getPost } from '@/modules/cms/client/api/cms.api';
import Board from '@/modules/core/client/components/Board';
import LoadingIndicator from '@/modules/core/client/components/LoadingIndicator';

/**
 * Format a post date in the reader's language.
 *
 * @param {String} value - ISO date
 * @param {String} language - i18n language
 * @returns {String}
 */
function formatDate(value, language) {
  if (!value) {
    return '';
  }
  return new Date(value).toLocaleDateString(language, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * A single public blog post.
 *
 * The body is server sanitized on both write and read, which is what makes
 * `dangerouslySetInnerHTML` acceptable here — see
 * `modules/cms/server/services/cms-sanitize.server.service.js`.
 */
export default function BlogPost({ slug }) {
  const { t, i18n } = useTranslation('cms');

  const [post, setPost] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function fetchPost() {
      setIsLoading(true);
      setNotFound(false);

      try {
        const data = await getPost(slug, { locale: i18n.language });

        if (!cancelled) {
          setPost(data);
        }
        // As with `CmsPage`, no URL rewrite on `movedFrom`: the old slug keeps
        // resolving, and rewriting under ui-router triggers a refetch.
      } catch (err) {
        if (!cancelled) {
          setNotFound(true);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    if (slug) {
      fetchPost();
    }

    return () => {
      cancelled = true;
    };
  }, [slug, i18n.language]);

  useEffect(() => {
    if (post && post.title) {
      document.title = post.metaTitle || post.title;
    }
  }, [post]);

  if (isLoading) {
    return <LoadingIndicator />;
  }

  if (notFound || !post) {
    return (
      <section className="container container-spacer">
        <h2>{t('Post not found')}</h2>
        <p className="lead">
          {t('This post does not exist, or is not published yet.')}
        </p>
        <p>
          <a href="/blog">← {t('All posts')}</a>
        </p>
      </section>
    );
  }

  return (
    <>
      <Board names="forestpath" className="blog-header">
        <div className="container">
          <div className="row">
            <div className="col-xs-12 col-sm-10 col-sm-offset-1">
              <p className="blog-post-kicker">
                <a href="/blog">{t('Blog')}</a>
                {post.category && (
                  <>
                    {' / '}
                    <a href={`/blog/category/${post.category.slug}`}>
                      {post.category.name}
                    </a>
                  </>
                )}
              </p>
              <h2>{post.title}</h2>
              <p className="blog-post-meta">
                <time dateTime={post.publishAt}>
                  {formatDate(post.publishAt, i18n.language)}
                </time>
                {post.author && post.author.displayName && (
                  <>
                    {' · '}
                    {post.author.displayName}
                  </>
                )}
              </p>
            </div>
          </div>
        </div>
      </Board>
      <section className="container container-spacer blog-post">
        <div className="row">
          <div className="col-xs-12 col-sm-10 col-sm-offset-1">
            {post.coverImage && (
              <figure className="blog-post-cover">
                <img src={post.coverImage} alt="" />
              </figure>
            )}
            <article
              className="cms-page-body"
              dangerouslySetInnerHTML={{ __html: post.body }}
            />
            {post.tags && post.tags.length > 0 && (
              <p className="blog-post-tags">
                {post.tags.map(tag => (
                  <a
                    key={tag}
                    href={`/blog?tag=${encodeURIComponent(tag)}`}
                    className="label label-default"
                  >
                    {tag}
                  </a>
                ))}
              </p>
            )}
            <p className="blog-post-back">
              <a href="/blog">← {t('All posts')}</a>
            </p>
          </div>
        </div>
      </section>
    </>
  );
}

BlogPost.propTypes = {
  slug: PropTypes.string,
};
